import React, { useState, useEffect } from 'react';
import withInfiniteScroll from 'global/components/withInfiniteScroll';
import { useSelector, useDispatch } from 'react-redux';
import { buildFilters } from 'global/utils/filter';
import { get } from 'global/lib/lodash';
import { List, ListItem, ListItemText, ListItemIcon } from '@mui/material';
import TaskListItem from './TaskListItemAging';
import TableHeader from './TableHeader';
import { formatDateSimple } from 'global/utils/formatDate';
import { Loading, useScreen } from 'global/styles';
import useStyles from './TaskListAging.styles';
import useRetrieveData from 'global/hooks/useRetrieveData';
import Print from 'global/components/print';
import { setTaskList } from 'redux/actions/view';
import TaskNotify from '../TaskNotify';
import { getRealmName } from 'global/utils';
import withSearchBar from 'global/components/withSearchBar';
import { Text } from 'global/components/text';
import PerformTasksFilters from '../Filters';
import clsx from 'clsx';
import { FilterChips } from 'global/components/filterchips';

const Includes = [
  'authors',
  'assignees',
  'viewers',
  'assessment',
  'realm',
  'realm.parent',
  'scorecardScore',
  // 'checkpoint',
];
const pageSize = 25;

const TableRows = [
  // { title: '', width: '5%', style: { textAlign: 'center' }, val: item => get(item, 'icon') },
  { title: 'Location', width: '30%', val: (item) => getRealmName(item, item.label) },
  { title: 'Author', width: '25%', val: (item) => get(item, 'author'), canDisplay: ({ filter }) => get(filter, 'content') === 'users' },
  { title: 'Assignee', width: '25%', val: (item) => get(item, 'assignee'), canDisplay: ({ filter }) => get(filter, 'content') === 'users' },
  { title: 'Title', width: '50%', val: (item) => get(item, 'name'), canDisplay: ({ filter }) => get(filter, 'content') === 'title' },
  { title: 'Type', width: '10%', val: (item) => get(item, 'status') },
  { title: 'Age', width: '10%', val: (item) => get(item, 'agingDate') },
];

let printPromise = {};
export default (props) => {
  const include = Includes.join(',');
  const filter = useSelector((state) => get(state, 'filter.tasks.age'));
  const realmId = useSelector((state) => get(state, 'auth.realm.id'));
  const [urlParams, setUrlParams] = useState({ waitFor: null, build: ['tasks'], params: { page: { size: pageSize, number: 1 }, include } });
  const { isMobile } = useScreen();
  const [tcountParams, setTcountParams] = useState({ normalize: false, waitFor: null });
  const classes = useStyles();
  const dispatch = useDispatch();
  const [printing, _printing] = useState(false);
  const [HeaderColumns, _HeaderColumns] = useState([
    { title: 'User', val: (p) => get(p, 'sState.auth.user.email', 'N/A') },
    { title: 'Location', val: (p) => get(p, 'sState.auth.realm.name', 'Any') },
    { title: 'Date', val: (p) => formatDateSimple(Date.now()) },
    { title: '0-6 days', val: (p) => get(p, 'sState.user', Loading) },
    { title: '7-20 days', val: (p) => get(p, 'sState.user', Loading) },
    { title: '21+ days', val: (p) => get(p, 'sState.user', Loading) },
  ]);
  const newTasks = useSelector((state) => get(state, 'view.task.new'));

  const getFilters = (extra = {}) => {
    return buildFilters(
      {
        ...filter,
        ...extra,
      },
      {
        convertName: { searchstring: 'name', realm_type: 'tier' },
        includeRealm: true,
        dontFilter: ['status', 'content'],
        overrideFilter: (_filtObj) => {
          const filtObj = { ..._filtObj };
          if (filtObj.vendor_id === 0) {
            delete filtObj.vendor_id;
          }

          if (filtObj.age === 0) {
            delete filtObj.age;
          }

          return filtObj;
        },
      }
    );
  };

  const tasks = useRetrieveData({ url: '/tasks' }, urlParams);
  const tcounts = useRetrieveData({ url: `/tasks` }, tcountParams);

  const setFilters = (clear) => {
    if (clear) {
      tasks.clearData();
    }
    setUrlParams({
      ...urlParams,
      useEffect: true,
      waitFor: undefined,
      params: {
        ...urlParams.params,
        ...getFilters({ open: true }),
        page: { size: pageSize, number: 1 },
      },
    });
    setTcountParams({
      ...tcountParams,
      useEffect: true,
      waitFor: undefined,
      params: {
        returnMeta: true,
        ...getFilters({ open: true }),
      },
    });
  };

  useEffect(() => {
    dispatch(setTaskList(tasks.data));
  }, [tasks.data]);

  useEffect(() => {
    setFilters(false);
  }, [filter, realmId]);

  useEffect(() => {
    if (printing && !tcounts.loading && !tasks.loading) {
      _printing(false);
      printPromise.resolve();
    }
  }, [tcounts.loading, tasks.loading]);

  useEffect(() => {
    const cols = HeaderColumns.map((col) => {
      if (col.title.indexOf('days') > -1) {
        const _var = col.title.indexOf('0-6') > -1 ? 'zeroSix' : col.title.indexOf('7-20') > -1 ? 'sevenTwenty' : 'twentyOnePlus';
        col.val = () => (tcounts.loading ? Loading : get(tcounts, ['data', 'data', _var], Loading));
      }
      return col;
    });
    _HeaderColumns(cols);
  }, [tcounts.data, tcounts.loading]);

  const lastPage = get(tasks, 'meta.page.last-page', null);
  const updatePage = (newPage) => {
    if (newPage <= lastPage) {
      setUrlParams({
        ...urlParams,
        params: {
          ...urlParams.params,
          page: { size: pageSize, number: newPage },
        },
      });
    }
  };

  const onPrint = () => {
    return new Promise((resolve, reject) => {
      printPromise.resolve = resolve;
      printPromise.reject = reject;

      const LIMIT = 1000;
      const totalCount = tcounts.loading ? LIMIT + 1 : get(tcounts, 'data.data.total');

      if (totalCount > LIMIT) {
        reject();
        return alert(`Too many tasks to print. Please limit your search results to a maximum of ${LIMIT}.`);
      }

      const params = { ...urlParams };
      delete params.params.page;
      setUrlParams({
        ...params,
        useEffect: false,
      });
      _printing(true);
    });
  };

  const tableRows = TableRows.filter((tr) => (tr.canDisplay ? tr.canDisplay({ filter }) : true));

  return (
    <>
      {!isMobile && <Print onPrint={onPrint} onFinish={() => setFilters(true)} className={classes.printBtn} />}
      <TaskNotify />

      <div className={clsx(classes.padLeft)}>
        <Text type={'headline_medium'}>{`Open ${isMobile ? '' : 'Tasks '}Report`}</Text>
        {isMobile ? <PerformTasksFilters {...props} /> : <FilterChips id={'tasks.age'} />}
      </div>

      <ListWithPages
        {...props}
        handlePageChange={updatePage}
        currentPage={get(urlParams, 'params.page.number', 1)}
        lastPage={lastPage}
        loading={tasks.loading}
        data={tasks.data}
        tableRows={tableRows}
        trackChecks={newTasks}
        header={
          <>
            <TableHeader columns={HeaderColumns} />
            <ListItem component={'div'} disableGutters={true} className={classes.tableHeader}>
              <ListItemIcon style={{ width: '56px' }} />
              {isMobile ? (
                <ListItemText secondary={'Task Info'} />
              ) : (
                <ListItemText
                  secondaryTypographyProps={{ component: 'div' }}
                  secondary={
                    <div className={classes.tableRow}>
                      {tableRows.map((tr, i) => (
                        <div key={i} className={classes.tableRow} style={{ width: tr.width }}>
                          {tr.title}
                        </div>
                      ))}
                    </div>
                  }
                />
              )}
            </ListItem>
          </>
        }
      />
    </>
  );
};

const ListWithPages = withSearchBar(withInfiniteScroll(TaskListItem, List));
